import Account from "../account.js";
import LanguageBehavior from "../language-behavior.js";
import Selection from "../selection.js";
import Uuid from "../utils/uuid.js";
import {findCommonAncestor, injectScript, injectStyles, isLocationHashUsingRouting, removeElement, stripTags} from "../utils/document.js";
import {addHighContrastHighlightType, getHighlightToggle, HIGHLIGHT_MARK_CLASS, HIGHLIGHT_TOGGLE_CLASS} from "../utils/highlight.js";
import Citation from "../citation.js";
import {findDoiInString} from "../utils/doi.js";
import {citationData} from "../data/model/index.js";
import {TEMP_HIGHLIGHT_UUID} from "../constants/general.js";

/**
 * Where not to disable the pjax library.
 *
 * @type {function(Object): boolean[]}
 */
const pjaxExceptions = [
// @ts-expect-error TS(7006) FIXME: Parameter 'location' implicitly has an 'any' type.
	location => location.host === "bitbucket.org"
];

// These are exported only for unit tests.
// Don't use them outside this library.
export const HIGHLIGHT_COLOR_TYPE_YELLOW = "yellow";
export const HIGHLIGHT_COLOR_TYPE_TOPIC_COLOR = "topicColor";
export const HIGHLIGHT_COLOR_TYPE_HIGH_CONTRAST = "highContrast";

const HIGH_CONTRAST_PDF_BODY_CLASS = "pn-high-contrast-mode";

class WebsiteBehaviors {
	_defaultInitialRenderDelayMs = 3000;

	/**
	 *
	 * @param {Url}      location
	 * @param {Object}   hooks
	 * @param {function} hooks.injectStyles       Force styles injection (main app).
	 * @param {function} hooks.renderAnnotations  Force rendering of annotations.
	 */
	// @ts-expect-error TS(7006) FIXME: Parameter 'location' implicitly has an 'any' type.
	constructor(location, hooks?) {
		(this as any).location = location;
		(this as any).hooks = hooks;
	}

	identifier() {
		return "WebsiteBehaviors";
	}

	/**
	 * Indicates whether this site uses the PDF viewer.
	 */
	static isPDF(): boolean {
		return false;
	}

	/**
	 * Returns true if the given url matches this site.
	 *
	 * @param {Object} location Contains "host", "href" and other properties, similar to window.location.
	 * @returns {boolean}
	 */
	static test(location: Location): boolean {
		throw new Error("This method should be extended");
	}

	/**
	 * Used for things like injecting special CSS.
	 */
	async init() {
		this._injectStyles();
		this._disablePjax();
	}

	/**
	 * Run code in the "main world" (content scripts normally run in "isolated world", which doesn't get full access to
	 * the page, but also loses some of the extension APIs we use).
	 *
	 * It is assumed that this runs before the init() function is called.
	 *
	 * Keep this minimal to avoid interference with the page.
	 */
	initMainWorld(): void {
		return;
	}

	/**
	 * Called from init() to inject site-specific styles into the page.
	 */
	_injectStyles() {
		if ((this as any).stylesheetRef) { // element already created (this isn't the first call to init())
			// @ts-expect-error TS(2531) FIXME: Object is possibly 'null'.
			if (!window.document.querySelector("body").contains((this as any).stylesheetRef))
			// @ts-expect-error TS(2531) FIXME: Object is possibly 'null'.
				window.document.querySelector("body").appendChild((this as any).stylesheetRef);
		} else {
			const styles = this.styles();
			if (styles)
				(this as any).stylesheetRef = injectStyles(document, styles);
		}
	}

	/**
	 * Called from init() to disable the pjax library on any page not on the exceptions list.
	 */
	_disablePjax() {
		let disablePjax = true;
		for (const rule of pjaxExceptions) {
			if (rule((this as any).location)) {
				disablePjax = false;
				break;
			}
		}
		if (disablePjax) {
			// Disable the jQuery "pjax" library, which does something similar to turbolinks (https://github.com/defunkt/jquery-pjax).
			// This needs to happen only on the appropriate frame, which is why this piece of code is here.
			injectScript(document, function () {
				const disablePjax = () => {
					if ((window as any).jQuery !== undefined && (window as any).jQuery.support.pjax)
						(window as any).jQuery.pjax.disable();
				};

				for (let i = 0; i < 20; i++)
					setTimeout(disablePjax, i * 1000);
			});
		}
	}

	/**
	 * Returns true to force downloading the PDF
	 *
	 * @returns {boolean}
	 */
	isPDFDownloadable() {
		return false;
	}

	/**
	 * @see init()
	 */
	destroy() {
		if ((this as any).stylesheetRef)
			removeElement(document, (this as any).stylesheetRef);
	}

	/**
	 * Code that runs in the top frame, when an iframe has taken control.
	 *
	 * @return {Object|null} Optionally return extra data to be sent to the frames as msgs.
	 */
	async topFrameHandler() {
		return null;
	}

	/**
	 * @return {null|string} CSS styles
	 */
	styles(): null | string {
		return null;
	}

	/**
	 * Called on iframes (never on the top frame). Controls whether the sidebar should be moved to
	 * the given iframe.
	 *
	 * @return {boolean}
	 */
	shouldFrameTakeControl() {
		return false;
	}

	/**
	 * Delay when initializing until we render page annotations.
	 *
	 * This is necessary because many websites use JS tricks to render the pages,
	 * such as hash routing, so we need to wait for them to complete before the DOM
	 * for the annotations exist.
	 *
	 * @returns {number} Milliseconds.
	 */
	initialRenderDelay() {
		return this._defaultInitialRenderDelayMs; // TODO: change to a low value (e.g. 100) when browser-ext uses highlighter.ts from highlight-lib
	}

	setDefaultInitialRenderDelayMs(value: number) {
		this._defaultInitialRenderDelayMs = value;
	}

	/**
	 * Used to customize attributes in an annotation, such as `selectedText`, which
	 * might come not from the actual selected text in the page.
	 *
	 * Changes should be made directly to the passed object.
	 *
	 * @param {Object} annotation
	 * @returns {void}
	 */
	// @ts-expect-error TS(7006) FIXME: Parameter 'annotation' implicitly has an 'any' typ... Remove this comment to see the full error message
	alterNewHighlight(annotation) {
	}

	/**
	 * Anything that needs to happen on the mousedown event.
	 *
	 * @param {Event} event
	 * @param {boolean} bypassHighlightRules
	 */
	// @ts-expect-error TS(7006) FIXME: Parameter 'event' implicitly has an 'any' type.
	mouseDown(event, bypassHighlightRules) {
	}

	/**
	 * Behavior on the mouseup event, where it is decided whether to show the annotation popup.
	 *
	 * @param {Event} event
	 * @param {boolean} bypassHighlightRules
	 * @param {boolean} isSelectionEqual
	 * @param {boolean} isPageCoordEqual
	 * @param {Page} page
	 * @param {function} processOrgProxy
	 * @returns {Promise<boolean>} Whether a selection was made.
	 */
	// @ts-expect-error TS(7006) FIXME: Parameter 'event' implicitly has an 'any' type.
	async mouseUp(event, bypassHighlightRules, isSelectionEqual, isPageCoordEqual, page, processOrgProxy) {
		const lang = document.documentElement.lang;
		const languageRules = LanguageBehavior.getLanguageRules(lang);

		// Apply adjustments and validate the selection prior to continuing
		const validSelection
			= Selection.ProcessAndValidateSelection(languageRules, bypassHighlightRules);

		// Display menu after a text selection has been made.
		// TODO: only validSelection needs to be in here, the rest should probably
		// be dealt with in pn-browser-ext
		return validSelection && !(isSelectionEqual && isPageCoordEqual);
	}

	/**
	 * Label used for the copy button, changed to indicate whether the copied text will include
	 * a citation.
	 *
	 * @returns {string}
	 */
	getCopyButtonLabel() {
		return "Copy";
	}

	/**
	 * @param {string} selectedText
	 * @param {Object} document
	 * @param {string} citationStyle E.g. "apa"
	 * @returns {Promise<boolean|void>} Return `false` to stop the "copied to clipboard" message from showing.
	 */
	// @ts-expect-error TS(7006) FIXME: Parameter 'selectedText' implicitly has an 'any' t... Remove this comment to see the full error message
	async copySelectionToClipboard(selectedText, document, citationStyle) {
		// @ts-expect-error TS(7006) FIXME: Parameter 'text' implicitly has an 'any' type.
		const getDummyTextarea = text => {
			const textarea = document.createElement("textarea");
			textarea.value = text;
			textarea.style.position = "absolute";
			textarea.style.top = -1000;
			textarea.style.left = -1000;

			return textarea;
		};

		try {
			const freeformCitation = await this.generateFreeformCitation(citationStyle);
			if (freeformCitation && (freeformCitation as any).citation) {
				const stringCitation = stripTags((freeformCitation as any).citation);
				if (stringCitation)
					selectedText += "\n\n" + stringCitation;
			}
		} catch (e) {
			// Nothing to do here
		}

		const dummyTextarea = getDummyTextarea(selectedText);
		document.body.append(dummyTextarea);
		dummyTextarea.select();

		// The selected text is either what the user actually selected prior to the
		// highlight popup appearing, or the dummy text area for special cases.
		// We always copy the text, then do special handling as needed, below.
		document.execCommand("copy");

		dummyTextarea.remove();
	}

	/**
	 * Returns true if the site will use structured citations (using Zotero, CSL, and RIS formats), or false if it will
	 * only use a single HTML field.
	 *
	 * @return {boolean}
	 */
	isCitationStructured() {
		return true;
	}

	/**
	 * Returns the page's citation as a single HTML string. Only used if isCitationStructured() is false.
	 * Style should ideally match .csl filenames in https://github.com/citation-style-language/styles-distribution, but
	 * can also be an arbitrary string (e.g. "AGLC 4th ed.", from the Hein site).
	 *
	 * Useful for searching for CSL styles: https://csl.mendeley.com/searchByName
	 *
	 * @param {string} preferredStyle E.g. "modern-language-association"
	 */
	async generateFreeformCitation(preferredStyle: string): Promise<{style?: string; citation: string}> {
		throw new Error("Not implemented");
	}

	/**
	 * Returns structured v2 citation data from the current page, or `null` to use the default methods.
	 *
	 * @param {string} url Only used when we do things very manually.
	 */
	async generateStructuredCitation(url: string): Promise<citationData | null> {
		let doiElement = document.querySelector("meta[name=citation_doi]");
		if (!doiElement)
			doiElement = document.querySelector("meta[name=doi]");
		if (doiElement) {
			const doi = findDoiInString(doiElement.getAttribute("content"));
			if (doi) {
				const citation = await Citation.fromDOI(doi);
				if (citation)
					return citation;
			}
		}

		let pmidElement = document.querySelector("meta[name=citation_pmid]");
		if (!pmidElement)
			pmidElement = document.querySelector("meta[name=pmid]");
		if (pmidElement) {
			// @ts-expect-error FIXME
			const matches = /\d+/.exec(pmidElement.getAttribute("content"));
			if (matches && matches[0].length > 0) {
				const pmid = matches[0];
				const citation = await Citation.fromPMID(pmid);
				if (citation)
					return citation;
			}
		}

		let isbnElement = document.querySelector("meta[name=citation_isbn]");
		if (!isbnElement)
			isbnElement = document.querySelector("meta[name=isbn]");
		if (isbnElement) {
			// @ts-expect-error FIXME
			const matches = /\d+/.exec(isbnElement.getAttribute("content"));
			if (matches && matches[0].length > 0) {
				const isbn = matches[0];
				const citation = await Citation.fromISBN(isbn);
				if (citation)
					return citation;
			}
		}

		return Citation.fromMetaTags(document, url);
	}

	/**
	 * Structured citations should usually be generated from the top-level frame,
	 * but for some sites (particularly when running a pdf viewer in an embedded iframe)
	 * it is desirable to generate the citation from the frame that is actively running powernotes.
	 * This function controls whether the top frame or the active frame generates citations.
	 *
	 * @return {boolean}
	 */
	structuredCitationFromTopFrame() {
		return true;
	}

	/**
	 * Gets the URL for the current page.
	 *
	 * If a URL is found in the `data-pn-url` attribute in the DOM (in the <html> element), returns that one instead of
	 * `window.location.href`.
	 *
	 * Runs the URL through the passed `processOrgProxy` callback, unless it is overridden and "final" (marked by the
	 * presence of the `data-pn-url-final` attribute in the markup, but this is only considered if "isClean" is false).
	 *
	 * @param {function} processOrgProxy
	 * @param {boolean} isClean
	 * @return {string}
	 */
	// @ts-expect-error TS(7006) FIXME: Parameter 'processOrgProxy' implicitly has an 'any... Remove this comment to see the full error message
	getPageUrl(processOrgProxy, isClean = false) {
		let url;

		url = document.documentElement.getAttribute("data-pn-url");
		if (url) {
			if (!isClean && document.documentElement.getAttribute("data-pn-url-final"))
				return url;
			else
				return processOrgProxy(url);
		}

		url = window.location.href;

		if (!isLocationHashUsingRouting(window.location.hash)) {
			// Removes everything after '#' at the end of the URL.
			url = url.replace(/#.*$/, "");
		}

		return processOrgProxy(url);
	}

	/**
	 * Finds the start and end nodes and offsets in the page for the current selection object.
	 *
	 * Returns:
	 *   {
	 *       startData: {
	 *           node: Node,
	 *           offset: number,
	 *       },
	 *       endData: {
	 *           node: Node,
	 *           offset: number,
	 *       },
	 *   }
	 *
	 * @param {Object} selection
	 * @param {string} selection.startTreeStr E.g. "div(1)/div(2)/mark(24)"
	 * @param {number} selection.startOffset
	 * @param {string} selection.endTreeStr Similar to startTreeStr
	 * @param {number} selection.endOffset
	 * @param {string} uuid
	 * @return {Object|null} Null means the highlight cannot be rendered.
	 */
	// @ts-expect-error TS(7006) FIXME: Parameter 'selection' implicitly has an 'any' type... Remove this comment to see the full error message
	findHighlightBoundaries(selection, uuid) {
		const warnCantRender = function () {
			console.warn("Annotation can't be rendered. (" + uuid + ")");
			return null;
		};

		if (!selection?.startTreeStr)
			return warnCantRender(); // We don't understand other selection methods here

		const startData = Selection.GetCorrectNodeAndOffset(selection.startTreeStr, selection.startOffset, "start");
		if (!startData)
			return warnCantRender();

		const endData = Selection.GetCorrectNodeAndOffset(selection.endTreeStr, selection.endOffset, "end");
		if (!endData)
			return warnCantRender();

		if (Selection.GetNestedHighlight(selection, uuid)) {
			console.warn("Annotation NOT renderable due to overlap (" + uuid + ")");
			return null;
		}

		return {startData, endData};
	}

	/**
	 * Renders the given highlight to the document.
	 * This should only be called from render.js, in order to keep track of rendered highlights
	 * on the page.
	 *
	 * @param {Object} annotation
	 * @param {string} annotation.uuid
	 * @param {string} annotation.type
	 * @param {string} annotation.selection.startTreeStr E.g. "div(1)/div(2)/mark(24)"
	 * @param {number} annotation.selection.startOffset
	 * @param {string} annotation.selection.endTreeStr Similar to startTreeStr
	 * @param {number} annotation.selection.endOffset
	 * @param {Object} account User's account object
	 * @param {string} topicColor
	 * @return {string|null} Uuid if successful (might have been generated here); null if not.
	 */
	// @ts-expect-error TS(7006) FIXME: Parameter 'annotation' implicitly has an 'any' typ... Remove this comment to see the full error message
	renderHighlight(annotation, account, topicColor) {
		if (annotation.type !== "highlight")
			return null;

		const uuid = typeof annotation.uuid !== "undefined" ? annotation.uuid : Uuid.base64();

		const data = this.findHighlightBoundaries(annotation.selection, uuid);
		if (!data)
			return null;

		const marker = document.createElement("mark");
		marker.classList.add("aa-highlight");
		marker.classList.add(HIGHLIGHT_MARK_CLASS);
		marker.setAttribute("data-uuid", uuid);

		let startNode = data.startData.node;
		let endNode = data.endData.node;
		const startOffset = data.startData.offset;
		const endOffset = data.endData.offset;

		const commonAncestor = findCommonAncestor(startNode, endNode);

		// Is the highlight in a PDF on the invisible text layer? PDF.js's styles
		// set the text layer's opacity to 0.2, so we should use full opacity
		// colored highlights or else the highlights will be very hard to see
		const textLayer = (commonAncestor.nodeType === Node.ELEMENT_NODE)
			? commonAncestor.closest(".textLayer")
			: commonAncestor.parentNode.closest(".textLayer");
		const isInvisibleTextLayer = textLayer && textLayer.style.opacity < 1;

		// Add highlight class and/or color styles
		const highlightColorType = this.getHighlightColorType(account);

		// Add highlight class and/or color styles
		if (isInvisibleTextLayer) {
			const hasHcClass = document.body.classList.contains(HIGH_CONTRAST_PDF_BODY_CLASS);

			if (highlightColorType === HIGHLIGHT_COLOR_TYPE_HIGH_CONTRAST && !hasHcClass)
				document.body.classList.add(HIGH_CONTRAST_PDF_BODY_CLASS);
			else if (highlightColorType !== HIGHLIGHT_COLOR_TYPE_HIGH_CONTRAST && hasHcClass)
				document.body.classList.remove(HIGH_CONTRAST_PDF_BODY_CLASS);
		}

		// @ts-expect-error FIXME
		const setHighlightColor = (color) => {
			marker.style.backgroundColor = isInvisibleTextLayer && (highlightColorType !== HIGHLIGHT_COLOR_TYPE_HIGH_CONTRAST)
				? `#${color}`
				: `#${color}4d`;
		};

		const isTempHighlight = uuid === TEMP_HIGHLIGHT_UUID;
		if (isTempHighlight) {
			// Always render the temporary highlights in their special color
			setHighlightColor(topicColor);
		} else {
			switch (highlightColorType) {
				case HIGHLIGHT_COLOR_TYPE_HIGH_CONTRAST:
					// Highlight colors are set in pn-browser-ext with the class,
					// AND the textLayer opacity is adjusted to 1
					break;

				case HIGHLIGHT_COLOR_TYPE_YELLOW:
					marker.classList.add("aa-highlight-default");
					setHighlightColor("ffff00");
					break;

				case HIGHLIGHT_COLOR_TYPE_TOPIC_COLOR:
					marker.classList.add("aa-highlight-topic-color");
					setHighlightColor(topicColor);
					break;

				default:
					throw new Error("Unrecognized highlight color type");
			}
		}

		// Walk all nodes between start and end nodes in order to highlight selection.
		// @ts-expect-error TS(7006) FIXME: Parameter 'node' implicitly has an 'any' type.
		const walk = function (node) {
			const nodeType = node.nodeType;
			const nodeVsStart = startNode.compareDocumentPosition(node);
			const nodeVsEnd = endNode.compareDocumentPosition(node);

			const isNodeOnlyPrecedingStart = nodeVsStart === Node.DOCUMENT_POSITION_PRECEDING;
			const isNodeOnlyPrecedingEnd = nodeVsEnd === Node.DOCUMENT_POSITION_PRECEDING;
			const isNodeOnlyFollowingStart = nodeVsStart === Node.DOCUMENT_POSITION_FOLLOWING;
			const isNodeOnlyFollowingEnd = nodeVsEnd === Node.DOCUMENT_POSITION_FOLLOWING;

			if (
				(isNodeOnlyPrecedingStart && isNodeOnlyPrecedingEnd)
				|| (isNodeOnlyFollowingStart && isNodeOnlyFollowingEnd)
			)
				return;

			if (nodeType === Node.TEXT_NODE) {
				if (!isTempHighlight && highlightColorType === HIGHLIGHT_COLOR_TYPE_HIGH_CONTRAST)
					addHighContrastHighlightType(node, marker);

				if (nodeVsStart === 0) {
					const text = node.textContent;
					const node1 = document.createTextNode(text.substr(0, startOffset));
					let node2;

					if (nodeVsEnd !== 0) {
						const text2 = text.substr(startOffset);
						node2 = marker.cloneNode(false);
						node2.textContent = text2;
						const nodeParent = node.parentNode;

						nodeParent.replaceChild(node2, node);
						nodeParent.insertBefore(node1, node2);
					} else {
						const text2 = text.substr(startOffset, endOffset - startOffset);
						node2 = marker.cloneNode(false);
						node2.textContent = text2;
						const node3 = document.createTextNode(text.substr(endOffset));
						const nodeParent = node.parentNode;

						nodeParent.replaceChild(node3, node);
						nodeParent.insertBefore(node2, node3);
						nodeParent.insertBefore(node1, node2);

						// Original end node was removed, update reference
						// to it with new wrapped node.
						endNode = node2;
					}

					// Original start node was removed, update reference
					// to it with new wrapped node.
					startNode = node2;
				} else if (nodeVsEnd === 0) {
					const text = node.textContent;
					const text1 = text.substr(0, endOffset);
					const node2 = document.createTextNode(text.substr(endOffset));
					const node1 = marker.cloneNode(false);
					node1.textContent = text1;
					const nodeParent = node.parentNode;

					nodeParent.replaceChild(node2, node);
					nodeParent.insertBefore(node1, node2);
					// Original end node was removed, update reference
					// to it with new wrapped node.
					endNode = node1;
				} else if (isNodeOnlyFollowingStart && isNodeOnlyPrecedingEnd) {
					const text = node.textContent;
					const newNode = marker.cloneNode(false);
					newNode.textContent = text;
					const nodeParent = node.parentNode;

					nodeParent.replaceChild(newNode, node);
				}
			} else if (nodeType === Node.ELEMENT_NODE) {
				const childNodes = node.childNodes;
				for (const childNode of childNodes)
					walk(childNode);
			}
		};

		walk(findCommonAncestor(startNode, endNode));

		// Add an invisible element before startNode for keyboard focus
		// First check if there already exists such a button
		if (getHighlightToggle(uuid))
			return uuid;

		// Create button, attach focus/blur handlers
		const invisibleButton = document.createElement("button");
		invisibleButton.classList.add(HIGHLIGHT_TOGGLE_CLASS);
		invisibleButton.setAttribute("data-uuid", uuid);
		invisibleButton.setAttribute("type", "button");
		invisibleButton.onfocus = function () {
			document.querySelectorAll(".aa-highlight[data-uuid='" + uuid + "']")
				.forEach(mark => mark.classList.add("aa-highlight-keyboard-focus"));
		};
		invisibleButton.onblur = function () {
			document.querySelectorAll(".aa-highlight[data-uuid='" + uuid + "']")
				.forEach(mark => mark.classList.remove("aa-highlight-keyboard-focus"));
		};
		invisibleButton.textContent = "Select highlight";

		// Insert button into DOM
		const startNodeParent = startNode.parentNode;
		if (startNodeParent.nodeName !== "A")
			startNodeParent.insertBefore(invisibleButton, startNode);
		else {
			const parentNextSib = startNodeParent.nextSibling;
			if (parentNextSib)
				startNodeParent.parentNode.insertBefore(invisibleButton, parentNextSib);
			else
				startNodeParent.parentNode.appendChild(invisibleButton);
		}

		return uuid;
	}

	/**
	 * Get the highlight color render type based on user preferences (safely).
	 *
	 * @param {Object} account User's account object
	 */
	// @ts-expect-error TS(7006) FIXME: Parameter 'account' implicitly has an 'any' type.
	getHighlightColorType(account) {
		const prefs = Account.getPrefsFromAccount(account);

		if (prefs.highContrastMode)
			return HIGHLIGHT_COLOR_TYPE_HIGH_CONTRAST;
		else
			return prefs.extension_highlightColor;
	}
}

export default WebsiteBehaviors;
